import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, GraduationCap, ClipboardList, FileQuestion,
  Newspaper, Video, CalendarCheck, CalendarClock, Mail, Settings, LogOut, Menu, X,
} from "lucide-react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import { AdminApplications, AdminRegistrations, AdminSubmissionsTable } from "./AdminSubmissions";
import { AdminCourses, AdminInsights, AdminVideos, AdminSettings } from "./AdminContent";
import AdminBookings from "./AdminBookings";

const TABS = [
  ["overview", "Overview", LayoutDashboard],
  ["applications", "Expert Applications", Users],
  ["bookings", "Expert Bookings", CalendarClock],
  ["courses", "Training Courses", GraduationCap],
  ["registrations", "Course Registrations", ClipboardList],
  ["requests", "Course Requests", FileQuestion],
  ["insights", "Insights", Newspaper],
  ["videos", "Videos", Video],
  ["consultations", "General Consultations", CalendarCheck],
  ["contacts", "Contact Inquiries", Mail],
  ["settings", "Website Settings", Settings],
];

function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  if (!stats) return <p className="text-sm text-slate-400">Loading...</p>;
  const items = [
    ["Pending expert applications", stats.applications_pending, "applications"],
    ["Published experts", stats.experts_published, "applications"],
    ["Published courses", stats.courses, "courses"],
    ["Course registrations", stats.registrations, "registrations"],
    ["New course requests", stats.course_requests, "requests"],
    ["Insight articles", stats.insights, "insights"],
    ["Videos", stats.videos, "videos"],
    ["New consultations", stats.consultations, "consultations"],
    ["New contact inquiries", stats.contacts, "contacts"],
  ];
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-overview">
      {items.map(([label, value]) => (
        <div key={label} className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
          <p className="font-display text-4xl font-semibold text-navy">{value}</p>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [menuOpen, setMenuOpen] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem("stratedge-admin-token")) {
      navigate("/admin");
      return;
    }
    api.get("/auth/me").then(() => setChecking(false)).catch(() => {
      localStorage.removeItem("stratedge-admin-token");
      navigate("/admin");
    });
  }, [navigate]);

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-slate-50 text-sm text-slate-400">Loading...</div>;

  const logout = () => {
    localStorage.removeItem("stratedge-admin-token");
    navigate("/admin");
  };

  const content = {
    overview: <Overview />,
    applications: <AdminApplications />,
    bookings: <AdminBookings />,
    courses: <AdminCourses />,
    registrations: <AdminRegistrations />,
    requests: <AdminSubmissionsTable kind="course-requests" title="Course Requests" />,
    insights: <AdminInsights />,
    videos: <AdminVideos />,
    consultations: <AdminSubmissionsTable kind="consultations" title="Consultation Requests" />,
    contacts: <AdminSubmissionsTable kind="contacts" title="Contact Inquiries" />,
    settings: <AdminSettings />,
  }[tab];

  const Sidebar = () => (
    <nav className="flex h-full flex-col" data-testid="admin-sidebar">
      <div className="border-b border-white/10 p-5"><Logo dark compact /></div>
      <div className="flex-1 overflow-y-auto p-3">
        {TABS.map(([key, label, Icon]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => { setTab(key); setMenuOpen(false); }}
            className={`mb-1 flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-start text-sm font-medium transition-colors ${
              tab === key ? "bg-gold text-navy" : "text-slate-300 hover:bg-white/5 hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" /> {label}
          </button>
        ))}
      </div>
      <div className="border-t border-white/10 p-3">
        <button onClick={logout} data-testid="admin-logout-button"
          className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-slate-300 hover:bg-white/5 hover:text-white">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-slate-100" data-testid="admin-dashboard">
      <aside className="fixed inset-y-0 start-0 z-40 hidden w-64 bg-navy-950 lg:block"><Sidebar /></aside>
      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-navy-950/60" onClick={() => setMenuOpen(false)} />
          <aside className="absolute inset-y-0 start-0 w-64 bg-navy-950"><Sidebar /></aside>
        </div>
      )}
      <div className="flex-1 lg:ms-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-5">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="admin-menu-toggle" aria-label="Menu">
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <h1 className="font-display text-xl font-semibold text-navy">{TABS.find(([k]) => k === tab)?.[1]}</h1>
          </div>
          <span className="rounded-full bg-gold-soft px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gold-hover">Admin</span>
        </header>
        <main className="p-5 lg:p-8">{content}</main>
      </div>
    </div>
  );
}
